'use client'

import {
  Pagination as PaginationComponent,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/ui/atoms/Pagination'
import { cn } from '@/utils/ui'
import React from 'react'
import { useTranslations } from 'next-intl'
import { PaginationProps } from './types'

export const Pagination: React.FC<PaginationProps> = ({
  className,
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
}) => {
  const t = useTranslations('public')

  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  const hasExtraPrevPages = currentPage - 1 > 1
  const hasExtraNextPages = currentPage + 1 < totalPages

  // Handle page navigation
  const navigateToPage = (pageNum: number) => {
    if (isLoading) return
    onPageChange(pageNum)
  }

  if (totalPages < 2) return null

  return (
    <div className={cn('my-12', className)} aria-busy={isLoading}>
      <PaginationComponent>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={(e) => {
                e.preventDefault()
                if (hasPrevPage && !isLoading) {
navigateToPage(currentPage - 1)
                }
              }}
              aria-disabled={!hasPrevPage || isLoading}
              className={!hasPrevPage || isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {t('previous')}
            </PaginationPrevious>
          </PaginationItem>

          {hasExtraPrevPages && (
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          )}

          {hasPrevPage && (
            <PaginationItem>
              <PaginationLink
                onClick={(e) => {
                  e.preventDefault()
                  if (!isLoading) {
    navigateToPage(currentPage - 1)
                  }
                }}
                className={isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {currentPage - 1}
              </PaginationLink>
            </PaginationItem>
          )}

          <PaginationItem>
            <PaginationLink
              isActive
              onClick={(e) => {
                e.preventDefault()
                if (!isLoading) {
                  navigateToPage(currentPage)
                }
              }}
              className={isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {currentPage}
            </PaginationLink>
          </PaginationItem>

          {hasNextPage && (
            <PaginationItem>
              <PaginationLink
                onClick={(e) => {
                  e.preventDefault()
                  if (!isLoading) {
navigateToPage(currentPage + 1)
                  }
                }}
                className={isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {currentPage + 1}
              </PaginationLink>
            </PaginationItem>
          )}

          {hasExtraNextPages && (
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          )}

          <PaginationItem>
            <PaginationNext
              onClick={(e) => {
                e.preventDefault()
                if (hasNextPage && !isLoading) {
                  navigateToPage(currentPage + 1)
                }
              }}
              aria-disabled={!hasNextPage || isLoading}
              className={!hasNextPage || isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {t('next')}
            </PaginationNext>
          </PaginationItem>
        </PaginationContent>
      </PaginationComponent>
    </div>
  )
}
